/**
 *
 * "Edit deck page to view and interact all components for deck editing"
 *
 *
 * @file   EditDeckPage.js
 * @author Lateral
 * @since  2023
 */

import { DeckCanvas } from 'components'
import { Box, Grid, Typography } from '@mui/material'
import BomTable from 'components/tables/bom/BomTable'
import React, { useEffect, useState, useMemo, useRef } from 'react'
import { usePrompt } from 'hooks'
import { useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'
import { CreateRevision } from './components/createRevision/CreateRevision'
import KitItemForm from './components/kitItemForm/KitItemForm'
import CreateDeckButtons from './components/createDeck/CreateDeckButtons'
import BigNumber from 'bignumber.js'
import { PanelPositionItems, PanelsItems, SideLinersItems } from 'models'
import { DeckMode } from 'common/deckMode'
import { loseDataWarning } from 'common/messaging'
import { MobileDeckCanvas } from 'components/mobileDeckCanvas/MobileDeckCanvas'
import { DeckLayoutContainer } from '../common/DeckLayoutContainer'
import { use2D3DViewSwitch } from 'hooks/use2D3DViewSwitch'
import { Comments } from '../deckLayout/components/Comments'
import { useSnackbar } from 'notistack'
import uniq from 'lodash.uniq'

function EditDeckPage() {
  /**
   * Generates edit deck page
   *
   * @function
   *
   * @returns {object} - Edit Deck page
   */
  const [selected, setSelected] = useState([])
  const [materialSelected, setMaterialSelected] = useState('')
  const { deckId, equipmentId, deck, setDeckRevision, history, database, deckRevision } = useCurrentDeckContext()
  const isControlPressedRef = useRef(false)
  const { enqueueSnackbar } = useSnackbar()

  const maxRevision = useMemo(() => {
    const deckRevisions = database.deckRevisions.filter((d) => d.DeckId == deckId && d.IsPublished)
    const maxRevision = deckRevisions.sort((a, b) => b.RevisionNumber - a.RevisionNumber)[0]

    const maxRevisions = deckRevisions
      .filter((d) => d.RevisionNumber === maxRevision?.RevisionNumber && !d.IsArchived)
      .sort((a, b) => b.MinorRevisionNumber - a.MinorRevisionNumber)
      .sort((a, b) => b.RevisionLevel - a.RevisionLevel)

    return maxRevisions[0]
  }, [deckId, database.deckRevisions])

  usePrompt(
    loseDataWarning,
    history.history.length > 1 ||
      maxRevision?.RevisionNumber > deckRevision?.RevisionNumber ||
      (maxRevision?.RevisionNumber === deckRevision?.RevisionNumber &&
        maxRevision?.MinorRevisionNumber > deckRevision?.MinorRevisionNumber),
    () => {
      const revisions = database.deckRevisions
        .filter((d) => d.DeckId == deckId && !d.IsArchived && d.RevisionNumber === maxRevision?.RevisionNumber)
        .sort((a, b) => b.MinorRevisionNumber - a.MinorRevisionNumber)
        .sort((a, b) => b.RevisionLevel - a.RevisionLevel)

      setDeckRevision(structuredClone(revisions[0]))
    }
  )

  useEffect(() => {
    history.addHistory(deckRevision)
  }, [deckId])

  const [viewType, setViewType] = use2D3DViewSwitch()

  const switchViewType = (e) => {
    e?.stopPropagation()
    setViewType((currentViewType) => (currentViewType === '3D' ? '2D' : '3D'))
  }

  //Saving deck revsion
  function saveDeckRevision(newDeckRevision) {
    setDeckRevision(newDeckRevision)
    history.addHistory(newDeckRevision)
  }

  //Changing kit material
  function onSelectChange(data, isControlPressed) {
    setSelected(data)
    setMaterialSelected('')
    isControlPressedRef.current = isControlPressed
  }

  //Action on undo button
  function onUndo() {
    const data = history.undoHistory()
    if (data) {
      setDeckRevision(data)
    }
  }

  //Action on redo button
  function onRedo() {
    const data = history.redoHistory()
    if (data) {
      setDeckRevision(data)
    }
  }

  //logic for when a table row is clicked in the BOM Table.
  function onBomClick(kit, eligibleMaterialNumbers, panelType) {
    if (selected.length) {
      let change = false
      let sideLinersAffected = []
      const panels = selected
        .filter((s) => s.Position)
        .sort((a, b) => {
          if (a.Position.Column > b.Position.Column) {
            return 1
          } else if (a.Position.Column < b.Position.Column) {
            return -1
          }
          if (a.Position.Row < b.Position.Row) {
            return -1
          } else if (a.Position.Row > b.Position.Row) {
            return 1
          } else {
            return 0
          }
        })

      if (panelType === 'panel' || panelType === 'other') {
        change = cleanupPanel(kit, panels, deckRevision, isControlPressedRef.current)
      }

      if (panelType === 'sideliner' || (panelType === 'other' && !panels.length)) {
        const sideLiners = selected.filter((s) => s.Height).sort((a, b) => a.Height - b.Height)
        sideLiners.map((selectedSideLiner_) => {
          const selectedSideLiner = deckRevision.SideLiners.find(
            (s) => s.StartPosition === selectedSideLiner_.StartPosition && s.Side === selectedSideLiner_.Side
          )
          if (
            eligibleMaterialNumbers.includes(selectedSideLiner.MaterialNumber) ||
            !deckRevision.Kits.includes(selectedSideLiner.MaterialNumber)
          ) {
            if (
              !sideLinersAffected.find(
                (s) => s.StartPosition === selectedSideLiner.StartPosition && s.Side === selectedSideLiner.Side
              )
            ) {
              const kitHeight = new BigNumber(kit.Size?.Height ?? 1)
              const start = new BigNumber(selectedSideLiner.StartPosition)
              if (start.plus(kitHeight).isGreaterThan(deckRevision.Size.Rows)) {
                return
                //if SideLiner is not the same size as the previous, space needs to be made/added.
              } else if (kitHeight.isLessThan(selectedSideLiner.Height)) {
                const firstHeight = start.plus(kitHeight).toNumber()
                const lastHeight = start.plus(selectedSideLiner.Height).toNumber()
                for (let i = firstHeight; i < lastHeight; i++) {
                  deckRevision.SideLiners.push(
                    new SideLinersItems({
                      StartPosition: i,
                      Height: 1,
                      Side: selectedSideLiner.Side
                    })
                  )
                }
              } else if (kitHeight.isGreaterThan(selectedSideLiner.Height)) {
                const endPoint = start.plus(kitHeight)
                const sideLinersToRemove = deckRevision.SideLiners.filter(
                  (s) =>
                    start.isLessThan(s.StartPosition) &&
                    endPoint.isGreaterThan(s.StartPosition) &&
                    s.Side === selectedSideLiner.Side
                )

                sideLinersAffected = sideLinersAffected.concat(sideLinersToRemove)

                sideLinersToRemove.map((s) => {
                  const currentHeight = new BigNumber(s.Height)
                  const currentStart = new BigNumber(s.StartPosition)

                  deckRevision.SideLiners = deckRevision.SideLiners.filter(
                    (s2) => !(currentStart.isEqualTo(s2.StartPosition) && s.Side === s2.Side)
                  )

                  if (endPoint.isLessThan(currentStart.plus(currentHeight))) {
                    for (let i = 0; i < currentStart.plus(currentHeight).minus(endPoint).toNumber(); i++) {
                      deckRevision.SideLiners.push(
                        new SideLinersItems({
                          Side: s.Side,
                          StartPosition: endPoint.plus(i).toNumber(),
                          Height: 1
                        })
                      )
                    }
                  }
                })
              }
              change = true
              selectedSideLiner.MaterialNumber = kit.MaterialNumber
              selectedSideLiner.Height = kitHeight.toNumber()
            }
          }
        })
      }

      if (change) {
        const clone = structuredClone(deckRevision)
        setDeckRevision(clone)
        history.addHistory(clone)
        setSelected([])
      }
    } else {
      setSelected([])
      setMaterialSelected((previous) => {
        if (previous === kit.MaterialNumber) {
          return undefined
        }
        return kit.MaterialNumber
      })
    }
  }

  // Action on deleting a panel
  function cleanupPanel(kit, panels, deckRevision, isControlPressed = false) {
    if (!kit?.Size) {
      return false
    }

    const sortedPanels = [...panels].sort((a, b) => {
      const rowDiff = a.Position.Row - b.Position.Row
      if (rowDiff === 0) {
        return a.Position.Column - b.Position.Column
      }
      return rowDiff
    })

    if (!sortedPanels.length) {
      return false
    }

    // Check if panels are sequential (for Control+Click multi-select)
    const isPanelsSequential = () => {
      if (sortedPanels.length === 1) return true

      for (let i = 1; i < sortedPanels.length; i++) {
        const prev = sortedPanels[i - 1]
        const curr = sortedPanels[i]
        const prevKit = deckRevision.Kits.find((k) => k.MaterialNumber === prev.MaterialNumber)
        const prevHeight = Number(prevKit?.Size?.Height ?? 1)
        const prevWidth = Number(prevKit?.Size?.Width ?? 1)

        // Check if current panel is adjacent to previous panel
        const isAdjacentColumn =
          curr.Position.Row === prev.Position.Row && curr.Position.Column === prev.Position.Column + prevWidth
        const isAdjacentRow =
          curr.Position.Column === prev.Position.Column && curr.Position.Row === prev.Position.Row + prevHeight

        if (!isAdjacentColumn && !isAdjacentRow) {
          return false
        }
      }
      return true
    }

    // Handle non-sequential panel selection (Control+Click)
    if (isControlPressed && !isPanelsSequential()) {
      const kitHeight = Number(kit.Size.Height ?? 1)
      const kitWidth = Number(kit.Size.Width ?? 1)

      if (!Number.isFinite(kitHeight) || kitHeight <= 0 || !Number.isFinite(kitWidth) || kitWidth <= 0) {
        enqueueSnackbar({
          message: 'Kit has invalid dimensions',
          variant: 'info'
        })
        return false
      }

      const hiddenMaintenanceState = JSON.stringify({ canShow: false, newFormat: true })

      // Replace each selected panel individually with the kit
      for (const panel of sortedPanels) {
        const panelKit = deckRevision.Kits.find((k) => k.MaterialNumber === panel.MaterialNumber)
        const panelHeight = Number(panelKit?.Size?.Height ?? 1)
        const panelWidth = Number(panelKit?.Size?.Width ?? 1)

        // Check if kit fits in the panel's space
        if (kitHeight > panelHeight || kitWidth > panelWidth) {
          enqueueSnackbar({
            message: `Kit (${kitHeight}x${kitWidth}) does not fit in panel at (${panel.Position.Row}, ${panel.Position.Column}) with size (${panelHeight}x${panelWidth})`,
            variant: 'info'
          })
          continue
        }

        // Replace the panel and any sub-panels it occupies
        for (let row = panel.Position.Row; row < panel.Position.Row + kitHeight; row++) {
          for (let col = panel.Position.Column; col < panel.Position.Column + kitWidth; col++) {
            const isAnchor = row === panel.Position.Row && col === panel.Position.Column
            const updatedPanel = new PanelsItems({
              MaintenanceState: isAnchor ? null : hiddenMaintenanceState,
              MaterialNumber: kit.MaterialNumber,
              Position: new PanelPositionItems({
                Row: row,
                Column: col
              })
            })

            const existingIndex = deckRevision.Panels.findIndex((p) => {
              return p.Position.Column === col && p.Position.Row === row
            })

            if (existingIndex === -1) {
              deckRevision.Panels.push(updatedPanel)
            } else {
              deckRevision.Panels.splice(existingIndex, 1, updatedPanel)
            }
          }
        }
      }

      return true
    }

    const firstPanel = sortedPanels[0]
    const lastPanel = sortedPanels[sortedPanels.length - 1]

    const kitHeight = Number(kit.Size.Height ?? 1)
    const kitWidth = Number(kit.Size.Width ?? 1)

    if (!Number.isFinite(kitHeight) || kitHeight <= 0 || !Number.isFinite(kitWidth) || kitWidth <= 0) {
      enqueueSnackbar({
        message: 'Kit has invalid dimensions',
        variant: 'info'
      })
      return false
    }

    let selectionHeight = 0
    let selectionWidth = 0
    let startRow = firstPanel.Position.Row
    let startColumn = firstPanel.Position.Column

    // Calculate selection bounds by accounting for each panel's kit dimensions
    const lastPanelKit = deckRevision.Kits.find((k) => k.MaterialNumber === lastPanel.MaterialNumber)
    const lastPanelHeight = Number(lastPanelKit?.Size?.Height ?? 1)
    const lastPanelWidth = Number(lastPanelKit?.Size?.Width ?? 1)

    selectionHeight = lastPanel.Position.Row - firstPanel.Position.Row + lastPanelHeight
    selectionWidth = lastPanel.Position.Column - firstPanel.Position.Column + lastPanelWidth

    // Special handling for single panel selection
    if (sortedPanels.length === 1) {
      selectionHeight = lastPanelHeight
      selectionWidth = lastPanelWidth
    } else if (selectionHeight === 1 && selectionWidth === 1 && deckRevision.Panels?.length) {
      const rows = deckRevision.Panels.map((panel) => panel.Position.Row)
      const columns = deckRevision.Panels.map((panel) => panel.Position.Column)
      const minRow = Math.min(...rows)
      const maxRow = Math.max(...rows)
      const minColumn = Math.min(...columns)
      const maxColumn = Math.max(...columns)

      startRow = Math.max(firstPanel.Position.Row, minRow)
      startColumn = Math.max(firstPanel.Position.Column, minColumn)

      if (startRow > maxRow || startColumn > maxColumn) {
        return false
      }

      selectionHeight = maxRow - startRow + 1
      selectionWidth = maxColumn - startColumn + 1
    }

    if (kitHeight > selectionHeight || kitWidth > selectionWidth) {
      enqueueSnackbar({
        message: 'Kit can not be added to the selected position',
        variant: 'info'
      })
      return false
    }

    if (selectionHeight % kitHeight !== 0 || selectionWidth % kitWidth !== 0) {
      enqueueSnackbar({
        message: 'Selection dimensions must be divisible by the kit dimensions',
        variant: 'info'
      })
      return false
    }

    const materials = uniq(sortedPanels.map((panel) => panel.MaterialNumber)).filter(Boolean)
    for (const material of materials) {
      const materialKit = deckRevision.Kits.find((item) => item.MaterialNumber === material)
      if (!materialKit) {
        continue
      }

      const materialHeight = Number(materialKit.Size?.Height ?? 1)
      const materialWidth = Number(materialKit.Size?.Width ?? 1)

      if (materialWidth > selectionWidth) {
        enqueueSnackbar({
          message: `The width(${materialWidth}) of Kit, ${material}, is bigger than width of selection`,
          variant: 'info'
        })

        return false
      }

      if (materialHeight > selectionHeight) {
        enqueueSnackbar({
          message: `The height(${materialHeight}) of Kit, ${material}, is bigger than height of selection`,
          variant: 'info'
        })

        return false
      }
    }

    const endRow = startRow + selectionHeight - 1
    const endColumn = startColumn + selectionWidth - 1
    const hiddenMaintenanceState = JSON.stringify({ canShow: false, newFormat: true })

    const replacePanel = (row, column, maintenanceState) => {
      const updatedPanel = new PanelsItems({
        MaintenanceState: maintenanceState,
        MaterialNumber: kit.MaterialNumber,
        Position: new PanelPositionItems({
          Row: row,
          Column: column
        })
      })

      const existingIndex = deckRevision.Panels.findIndex((panel) => {
        return panel.Position.Column === column && panel.Position.Row === row
      })

      if (existingIndex === -1) {
        deckRevision.Panels.push(updatedPanel)
        return
      }

      deckRevision.Panels.splice(existingIndex, 1, updatedPanel)
    }

    if (selectionHeight === 1 && selectionWidth === 1) {
      replacePanel(firstPanel.Position.Row, firstPanel.Position.Column, null)
      return true
    }

    for (let row = startRow; row <= endRow; row += kitHeight) {
      for (let column = startColumn; column <= endColumn; column += kitWidth) {
        for (let innerRow = row; innerRow < row + kitHeight; innerRow++) {
          for (let innerColumn = column; innerColumn < column + kitWidth; innerColumn++) {
            const isAnchor = innerRow === row && innerColumn === column
            replacePanel(innerRow, innerColumn, isAnchor ? null : hiddenMaintenanceState)
          }
        }
      }
    }

    return true
  }

  function onPanelClick(selected) {
    if (selected === 'cancel') {
      setSelected([])
    } else {
      setSelected(Array.isArray(selected) ? selected : [selected])
    }

    setMaterialSelected('')
  }

  //only show Create buttons if Equipment exists.
  if (equipmentId && (!deckRevision || !Object.entries(deckRevision).length) && !deck) {
    return (
      <Grid item xs={12} sm={5} sx={{ height: '5%', display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
        <CreateDeckButtons setDeckRevision={setDeckRevision} />
      </Grid>
    )
  }

  if (!deck) {
    return null
  }

  //if a Deck exists, but has no revision, show the Create Revision buttons.
  if (Object.entries(deck).length && (!deckRevision || !Object.entries(deckRevision).length)) {
    return (
      <>
        <Grid item xs={12} sm={5} sx={{ height: '5%', display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
          <CreateRevision deckRevision={deckRevision} />
        </Grid>
      </>
    )
  }

  if (Object.entries(deck).length <= 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography variant="h3">No Deck Revision found.</Typography>
      </Box>
    )
  }

  return (
    <DeckLayoutContainer
      headerTitle="Edit Deck"
      deckRevision={deckRevision}
      buttons={<CreateRevision deckRevision={deckRevision} />}
      Deck={
        <>
          {viewType === '3D' ? (
            <DeckCanvas
              data={deckRevision}
              selected={selected}
              materialSelected={materialSelected}
              onSelectChange={onSelectChange}
              saveDeckRevision={saveDeckRevision}
              mode={DeckMode.edit}
              onUndo={onUndo}
              onRedo={onRedo}
              isHistoryEmpty={history.isEmpty}
              isHistoryAtEnd={history.isAtEnd}
              onSwitchView={switchViewType}
              onPanelClick={onPanelClick}
            />
          ) : (
            <MobileDeckCanvas
              data={deckRevision}
              selected={selected}
              materialSelected={materialSelected}
              onSelectChange={onSelectChange}
              saveDeckRevision={saveDeckRevision}
              mode={DeckMode.edit}
              onUndo={onUndo}
              onRedo={onRedo}
              isHistoryEmpty={history.isEmpty}
              isHistoryAtEnd={history.isAtEnd}
              onSwitchView={switchViewType}
              onPanelClick={onPanelClick}
            />
          )}
        </>
      }
      topRightElement={
        <Box>
          <Box mb={1}>
            <KitItemForm
              key={materialSelected}
              deckRevision={deckRevision}
              setDeckRevision={saveDeckRevision}
              setMaterialSelected={setMaterialSelected}
              data={deckRevision.Kits.find((k) => k.MaterialNumber === materialSelected)}
              materialSelected={materialSelected}
            />
          </Box>
          <Box mb={1} mt={1}>
            <Comments deckRevision={deckRevision} />
          </Box>
          <BomTable
            fullHeight
            data={deckRevision}
            selected={selected}
            materialSelected={materialSelected}
            onClick={onBomClick}
            canGenerateColor={!deckRevision.IsPublished}
            setDeckRevision={setDeckRevision}
          />
        </Box>
      }
      bottomRightElement={null}
      topRightSx={{
        height: '100%'
      }}
    />
  )
}

export default EditDeckPage
